<template>
  <PmAppointmentsContainerPure
    v-if="propsAppointmentContainer"
    v-slot="{ packingContainer, constrainedStartDate, constrainedEndDate }"
    class="PmPurchaseOrder"
    v-bind="propsAppointmentContainer"
    :container-start-date="containerStartDate"
    :container-end-date="containerEndDate"
    :day-width="store.state.cssVar.variables.dayWidth.value"
    :content-can-be-hidden="props.contentCanBeHidden"
    :packed-element="props.packedElement"
    :update-group-visible="false"
    :items-for-content-bounds="
      artificialAppointments.itemsForContentBounds.value
    "
    :is-highlighted="isHighlighted"
    @show-details="showDetails"
    @jump="jumpToPurchaseOrderInSidebar"
  >
    <PmPackingElementPure
      v-for="appointment in artificialAppointments.appointments.value"
      :id="`appointment-${appointment.id}`"
      :key="appointment.id"
      v-slot="slotProps"
      :packing-container="packingContainer"
    >
      <PmAppointmentPure
        :container-start-date="constrainedStartDate"
        :container-end-date="constrainedEndDate"
        v-bind="appointment.props"
        :packed-element="slotProps.packedElement"
        :day-width="store.state.cssVar.variables.dayWidth.value"
      >
        <PmResource
          v-for="resource in appointment.resources"
          :id="resource.id"
          :key="resource.id"
          :edit-buttons-visible="persoplanState.state.value.matches('edit')"
          :container-start-date="containerStartDate"
        />
      </PmAppointmentPure>
    </PmPackingElementPure>
  </PmAppointmentsContainerPure>

  <div v-else class="PmPurchaseOrder">
    <!-- We need a empty div for packing in any case -->
  </div>
</template>

<script setup lang="ts">
/**
 * @note Don't use v-if on PmAppointmentsContainer, it might break packing!
 */
import { computed } from 'vue'
import { useStore } from 'vuex'

import { EVENT } from '@/constants/events'
import { CALENDAR_ITEM_TYPE, DATA_MODAL_TYPE } from '@/constants/persoplan'

import { parseServerDateString } from '@/utilities/date'
import { persoplanStateKey, injectStrict } from '@/utilities/inject'
import { useCachedQuery } from '@/composition/useCachedQuery'
import { useArtificialAppointments } from '@/composition/useArtificialAppointments'
import { getPurchaseOrderStatus } from '@/composition/usePurchaseOrderStatus'
import EventHub from '@/eventHub'

import type { PackingItem } from '@/functional/packing'

import PmAppointmentsContainerPure, {
  type Props as PropsAppointmentContainerPure,
} from '@/components/persoplan/PmAppointmentsContainer/PmAppointmentsContainerPure.vue'
import PmPackingElementPure from '@/components/persoplan/PmPackingContainer/PmPackingElementPure.vue'
import PmAppointmentPure from '@/components/persoplan/PmAppointment/PmAppointmentPure.vue'
import PmResource from '@/components/persoplan/PmResource/PmResource.vue'

import { PurchaseOrderDocument } from '../../../../generated/graphql'

export interface Props {
  containerStartDate: Date
  containerEndDate: Date
  id: number
  contentCanBeHidden?: boolean
  packedElement?: PackingItem
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'example', id: string): void
}>()

const store = useStore()
const persoplanState = injectStrict(persoplanStateKey)

const startDate = computed(() => {
  return parseServerDateString(purchaseOrder.value?.startDate)
})

const endDate = computed(() => {
  return parseServerDateString(purchaseOrder.value?.endDate)
})

const purchaseOrderQuery = useCachedQuery(PurchaseOrderDocument, () => {
  const queryVariables = store.getters['queryVariables/calendar']

  return {
    ...queryVariables,
    id: props.id,
  }
})

const purchaseOrder = computed(
  () => purchaseOrderQuery.result.value?.purchaseOrder
)

const isHighlighted = computed({
  get() {
    return store.getters['persoplan/isItemHighlighted']({
      id: props.id,
      type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
    })
  },
  set(value) {
    if (value === true) {
      store.commit('persoplan/addToHighlightedItems', {
        id: props.id,
        type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
      })
    } else {
      store.commit('persoplan/removeFromHighlightedItems', {
        id: props.id,
        type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
      })
    }
  },
})

const artificialAppointments = useArtificialAppointments({
  resources: () => purchaseOrder.value?.resources,
  containerStartDate: startDate,
  containerEndDate: endDate,
})

const propsAppointmentContainer = computed(() => {
  if (!purchaseOrder.value) return
  if (!startDate.value) throw new Error('startDate is undefined')
  if (!endDate.value) throw new Error('endDate is undefined')

  const result: PropsAppointmentContainerPure = {
    id: props.id,
    type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
    startDate: startDate.value,
    endDate: endDate.value,
    title: purchaseOrder.value?.caption ?? undefined,
    number: purchaseOrder.value?.number ?? undefined,
    purchaseOrderStatus: getPurchaseOrderStatus({
      id: purchaseOrder.value?.purchaseOrderState?.id,
    }),
  }

  return result
})

function showDetails() {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.PURCHASE_ORDER,
    id: props.id,
  })
}

function jumpToPurchaseOrderInSidebar() {
  EventHub.$emit(EVENT.SIDEBAR_PURCHASE_ORDER_SCROLL_INTO_VIEW, {
    id: props.id,
    type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
  })

  store.commit('persoplan/clearHighlightedItems')
  isHighlighted.value = true
}
</script>
